import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  SxProps,
  Toolbar,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styled } from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UsersTable from '../../components/UsersTable/UsersTable';
import UserDialog from '../../components/UserDialog/UserDialog';
import { GENERAL_FONT } from '../../constants/theme';
import { DS } from '../../components/PatientForms/designTokens';
import SideMenu from '../../components/SideMenu/SideMenu';
import PatientDetails, {
  exportButtonStyle,
} from '../../components/PatientDetails/PatientDetails';
import { RootState } from '../../store/types/sharedTypes';
import {
  countUsers,
  getAllPatients,
  getPatientDetails,
} from '../../store/actions/general';
import Loader from '../../components/Loader/Loader';
import { stringAvatar } from '../../lib/helpers/avatar';
import AddAdmin from '../../components/AddAdmin/AddAdmin';
import BackButton from '../../components/BackButton/BackButton';
import PopupAlert from '../../components/PopupAlert/PopupAlert';
import { AlertType } from '../../lib/interfaces';
import AddPatient from '../../components/AddPatient/AddPatient';
import { loadMessages } from '../../store/reducers/messageBoard';
import { AppDispatch } from '../../store';
import { useFlashFromLocation } from '../../lib/hooks/useFlashFromLocation';
import FlashSnackbar from '../../components/common/FlashSnackbar';
import { hasPermission } from '../../lib/authz';
import TemplateManagementPage from '../../components/Template/TemplatePage';
import AppVersionManager from '../../components/AppVersionManager/AppVersionManager';
import VisitRunManager from '../../components/VisitRunManager/VisitRunManager';
import FormsExportManager from '../../components/FormsExportManager/FormsExportManager';

const PatientsTableContainer = styled.div`
  margin-left: 30px;
  height: calc(100vh - 190px);
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const drawerHeaderSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
});

const Dashboard = () => {
  const [drawerWidth, setDrawerWidth] = useState(300);
  const [drawerOpened, setDrawerOpened] = useState(true);
  const [addAdminModalOpened, setAddAdminModalOpened] = useState(false);
  const [addPatientModalOpened, setAddPatientModalOpened] = useState(false);
  const params = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showAlert, setShowAlert] = useState(false);

  const { open, msg, severity, handleClose, consumedAt } =
    useFlashFromLocation();

  const path = location.pathname.split('/')[2];

  const { sessionUser, apiLoading, apiError } = useSelector(
    (state: RootState) => state.general
  );

  const canCreateAdmin = hasPermission(sessionUser, 'admin:add');
  const canExportData = hasPermission(sessionUser, 'data:export');

  const appBarSx: SxProps<Theme> = (theme) => {
    if (drawerOpened) {
      return {
        marginLeft: `${drawerWidth}px`,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      };
    }
    return {
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    };
  };

  const contentSx: SxProps<Theme> = (theme) => {
    if (drawerOpened) {
      return {
        flexGrow: 1,
        minWidth: 0,
        padding: theme.spacing(3),
        marginLeft: 0,
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      };
    }
    return {
      flexGrow: 1,
      minWidth: 0,
      padding: theme.spacing(3),
      marginLeft: `${-drawerWidth}px`,
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    };
  };

  const drawerSx: SxProps<Theme> = {
    width: `${drawerWidth}px`,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: `${drawerWidth}px`,
      backgroundColor: DS.dawnSurface,
      borderRight: `1px solid ${DS.hairline}`,
    },
  };

  useEffect(() => {
    if (!consumedAt) return;
    if (path === 'patients' || path === undefined) {
      const getAdmins = false;
      dispatch(getAllPatients(getAdmins));
      dispatch(countUsers());
    }
  }, [consumedAt, path, dispatch]);

  useEffect(() => {
    const getAdmins = location.pathname.includes('admins');
    const patientId = params.id;

    if (!patientId) {
      dispatch(getAllPatients(getAdmins));
      dispatch(countUsers());
    } else {
      dispatch(getPatientDetails(patientId));
      dispatch(loadMessages(patientId));
    }
  }, [location.pathname, dispatch, params.id]);

  useEffect(() => {
    if (apiError) {
      setShowAlert(true);
    }
  }, [apiError]);

  const handleDrawerOpen = () => {
    setDrawerOpened(true);
    setDrawerWidth(300);
  };
  const handleDrawerClose = () => {
    setDrawerOpened(false);
    setDrawerWidth(0);
  };

  const [openUserDialog, setOpenUserDialog] = React.useState(false);

  const handleUserDialogOpen = () => {
    setOpenUserDialog(true);
  };

  const handleUserDialogClose = (value?: string) => {
    if (
      value &&
      (value.toLowerCase() === 'my profile' ||
        value.toLowerCase() === 'min profil')
    ) {
      navigate(`/dashboard/${sessionUser?._id}`);
    }
    setOpenUserDialog(false);
  };

  return (
    <>
      <div
        style={{ display: 'flex', backgroundColor: 'white', height: '100vh' }}
      >
        <AppBar
          style={{ background: 'white', color: 'black' }}
          position="fixed"
          sx={appBarSx}
        >
          <Toolbar>
            <IconButton
              size="large"
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 2,
                display: drawerOpened ? 'none' : undefined,
              }}
            >
              <MenuIcon />
            </IconButton>
            <BackButton />
            <Typography fontFamily={GENERAL_FONT} variant="h6" noWrap>
              {t('migrainehelper')}
            </Typography>
            <Avatar
              style={{
                marginRight: '0px',
                marginLeft: 'auto',
                cursor: 'pointer',
              }}
              {...stringAvatar(
                `${sessionUser?.firstName ?? ''} ${sessionUser?.lastName ?? ''}`
              )}
            />

            <Typography
              style={{
                marginRight: '0px',
                marginLeft: '12px',
                cursor: 'pointer',
              }}
              noWrap
              onClick={handleUserDialogOpen}
            >
              {`${sessionUser?.firstName} ${sessionUser?.lastName}`}
            </Typography>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={drawerSx}
          variant="persistent"
          anchor="left"
          open={drawerOpened}
        >
          <Box sx={drawerHeaderSx}>
            <IconButton onClick={handleDrawerClose} size="large">
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <SideMenu />
        </Drawer>
        {apiLoading ? (
          <Loader />
        ) : (
          <Box
            component="main"
            style={{
              backgroundColor: 'white',
              margin: 'auto',
              marginTop: '20px',
            }}
            sx={contentSx}
          >
            <Box sx={drawerHeaderSx} />
            {(() => {
              switch (path) {
                case 'admins':
                  return (
                    <>
                      <PageHeader>
                        <Typography
                          style={{
                            fontSize: 20,
                            fontWeight: 'bold',
                            fontFamily: GENERAL_FONT,
                          }}
                        >
                          {t('adminUsers')}
                        </Typography>
                        {canCreateAdmin && (
                          <Button
                            onClick={() => setAddAdminModalOpened(true)}
                            variant="contained"
                            sx={{ textTransform: 'none' }}
                            style={exportButtonStyle}
                          >
                            {t('addNewAdmin')}
                          </Button>
                        )}
                      </PageHeader>
                      <PatientsTableContainer>
                        <UsersTable admins rowsPerPage={12} />
                      </PatientsTableContainer>
                    </>
                  );
                case 'patients':
                  return (
                    <>
                      <PageHeader>
                        <Typography
                          style={{
                            fontSize: 20,
                            fontWeight: 'bold',
                            fontFamily: GENERAL_FONT,
                          }}
                        >
                          {t('allPatients')}
                        </Typography>
                        <Button
                          onClick={() => setAddPatientModalOpened(true)}
                          variant="contained"
                          sx={{ textTransform: 'none' }}
                          style={exportButtonStyle}
                        >
                          {t('addNewPatient')}
                        </Button>
                      </PageHeader>
                      <PatientsTableContainer>
                        <UsersTable rowsPerPage={12} />
                      </PatientsTableContainer>
                    </>
                  );
                case 'submissions':
                  return <> This is placeholder for submission requests page</>;
                case 'app-version':
                  return <AppVersionManager />;
                case 'visit-runs':
                  return <VisitRunManager />;
                case 'forms-export':
                  return canExportData ? <FormsExportManager /> : null;
                case 'template':
                case 'templates': {
                  return (
                    <Container
                      style={{ maxWidth: '1600px', minWidth: '1200px' }}
                    >
                      <TemplateManagementPage />
                    </Container>
                  );
                }
                case undefined:
                  return (
                    <>
                      <Typography
                        style={{
                          fontSize: 20,
                          fontWeight: 'bold',
                          fontFamily: GENERAL_FONT,
                        }}
                      >
                        {t('allPatients')}
                      </Typography>
                      <PatientsTableContainer>
                        <UsersTable rowsPerPage={12} />
                      </PatientsTableContainer>
                    </>
                  );
                default:
                  return (
                    <Container
                      style={{ maxWidth: '1600px', minWidth: '1200px' }}
                    >
                      <PatientDetails />
                    </Container>
                  );
              }
            })()}
          </Box>
        )}
      </div>

      <div>
        <UserDialog open={openUserDialog} onClose={handleUserDialogClose} />
      </div>
      <div>
        <AddAdmin
          modalOpen={addAdminModalOpened}
          onClose={() => setAddAdminModalOpened(false)}
        />
      </div>
      <div>
        <AddPatient
          modalOpen={addPatientModalOpened}
          onClose={() => setAddPatientModalOpened(false)}
        />
      </div>
      <div>
        <PopupAlert
          open={showAlert}
          message={apiError ?? ''}
          type={AlertType.Danger}
          onClose={() => setShowAlert(false)}
        />
      </div>
      <FlashSnackbar
        open={open}
        msg={msg}
        severity={severity}
        onClose={handleClose}
      />
    </>
  );
};

export default Dashboard;
